import { Editor, isDocEmpty, serializeToHTML } from "@smartsuite/smartdoc";
import { type Doc } from "@smartsuite/smartdoc/lib/esm/core/types";
import { ChecklistItemEditorRenderProps } from "@smartsuite/react-ui/lib/components/fields/Checklist/ChecklistField.types";
import { InputGroup } from "@smartsuite/react-ui/lib/components/forms/InputGroup/InputGroup";
import { checklistItemMaxLength } from "@smartsuite/types";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { createInternalSharedFiles, createSharedFiles } from "../../../../api";
import { createFilestackClient } from "../../../../config/filestack";
import { useFormContext } from "../../../../hooks/useFormContext";
import { $t } from "../../../../utils/intl";
import {
  FormsFilestackExtensionService,
  SaveSharedFiles,
} from "../../smartdoc/SmartdocFieldControl/filestack-extension-service";
import { createChecklistEditorExtensions } from "./createChecklistEditorExtensions";
import { texts } from "./ChecklistFieldControl.text";

import "./ChecklistItemEditor.sass";

export type ChecklistItemEditorProps = ChecklistItemEditorRenderProps;

/**
 * Injected smartdoc editor for a single checklist item. react-ui owns the row and the list;
 * this only adapts the smartdoc `Editor` to its render-prop contract: serialize Doc -> html
 * on change, report emptiness, and map Enter/Escape to finish/cancel. Uses the same
 * checklist extensions as the record editor (slash commands, floating toolbar, images);
 * mentions stay off because a public respondent has no member context.
 */
export const ChecklistItemEditor: React.FunctionComponent<ChecklistItemEditorProps> = ({
  content,
  autoFocus,
  isReadOnly,
  onContentChange,
  onEmptyChange,
  onFocus,
  onBlur,
  onEnter,
  onEscape,
}) => {
  const [focused, setFocused] = useState(false);
  const { accountId, config, isInternal, reportId, passcode } = useFormContext();

  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;

  const filestackService = useMemo(() => {
    let saveFiles: SaveSharedFiles | undefined;

    if (isInternal && reportId) {
      saveFiles = (files) => createInternalSharedFiles(accountId, reportId, files);
    } else if (accountId && config.sharing_hash) {
      saveFiles = (files) =>
        createSharedFiles(accountId, config.sharing_hash, files, false, passcode);
    }

    if (!saveFiles) {
      return undefined;
    }

    return new FormsFilestackExtensionService(
      createFilestackClient(config.filestack_api_key, { security: config.file_security }),
      saveFiles
    );
  }, [
    accountId,
    config.file_security,
    config.filestack_api_key,
    config.sharing_hash,
    isInternal,
    passcode,
    reportId,
  ]);

  const extensions = useMemo(
    () =>
      createChecklistEditorExtensions({
        placeholder: {
          placeholder: $t(texts.addNewItemPlaceholder),
          placeholderMode: "empty-node",
        },
        maxLength: checklistItemMaxLength,
        filestackService,
      }),
    [filestackService]
  );

  const handleContentChange = useCallback(
    (doc: Doc) => {
      onEmptyChange(isDocEmpty(doc));
      onContentChangeRef.current({ data: doc, html: serializeToHTML(doc, extensions) });
    },
    [onEmptyChange, extensions]
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
    onFocus();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    onBlur();
  }, [onBlur]);

  // Enter without shift commits the item and opens the next one; Escape cancels. Captured so
  // smartdoc does not insert a newline before we get to react.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        onEnter();
      }
      if (event.key === "Escape") {
        event.stopPropagation();
        onEscape();
      }
    },
    [onEnter, onEscape]
  );

  return (
    <div className="checklist-item-editor" onKeyDownCapture={handleKeyDown}>
      <InputGroup state={focused ? "active" : "default"} isReadOnly={isReadOnly}>
        <Editor
          extensions={extensions}
          initialDoc={content?.data ?? null}
          editable={!isReadOnly}
          autoFocus={autoFocus}
          onContentChange={handleContentChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </InputGroup>
    </div>
  );
};
